import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Participant reads of test questions. test_questions / test_question_options
 * are readable only by the content's organizers (they hold is_correct and
 * explanation), so participants go through SECURITY DEFINER RPCs:
 *  - get_test_for_taking: questions + options WITHOUT is_correct/explanation
 *  - get_test_review: one of the caller's own completed attempts, WITH the
 *    correct options and explanations
 *  - get_test_question_counts: question counts for test lists
 */

export interface TakingOption {
  id: string;
  question_id: string;
  option_text: string;
  sort_order: number | null;
}

export interface TakingQuestion {
  id: string;
  test_id: string;
  question_text: string;
  type: 'single_choice' | 'multiple_choice' | 'true_false' | 'essay';
  points: number | null;
  sort_order: number | null;
  options: TakingOption[];
}

export interface ReviewOption extends TakingOption {
  is_correct: boolean | null;
}

export interface ReviewQuestion extends Omit<TakingQuestion, 'options'> {
  explanation: string | null;
  options: ReviewOption[];
}

export interface TestReview {
  attempt_id: string;
  test_id: string;
  score: number | null;
  passed: boolean | null;
  answers: Record<string, string | string[]>;
  questions: ReviewQuestion[];
}

// The RPCs are newer than the generated Database types.
type AnyClient = SupabaseClient<any, any, any>;

export async function fetchTestForTaking(supabase: AnyClient, testId: string): Promise<TakingQuestion[]> {
  const { data, error } = await supabase.rpc('get_test_for_taking', { p_test_id: testId });
  if (error) throw error;
  return (Array.isArray(data) ? data : []) as TakingQuestion[];
}

export async function fetchTestReview(supabase: AnyClient, attemptId: string): Promise<TestReview | null> {
  const { data, error } = await supabase.rpc('get_test_review', { p_attempt_id: attemptId });
  if (error) throw error;
  if (!data || typeof data !== 'object') return null;
  const review = data as TestReview;
  return {
    ...review,
    answers: (review.answers as Record<string, string | string[]>) || {},
    questions: Array.isArray(review.questions) ? review.questions : [],
  };
}

export async function fetchTestQuestionCounts(supabase: AnyClient, testIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (testIds.length === 0) return counts;
  const { data, error } = await supabase.rpc('get_test_question_counts', { p_test_ids: testIds });
  if (error) throw error;
  for (const row of (data ?? []) as { test_id: string; question_count: number }[]) {
    counts.set(row.test_id, Number(row.question_count) || 0);
  }
  return counts;
}
